import React, { useCallback, useEffect, useRef, useState } from 'react';
import usePassengerService from '../../services/usePassengerService ';
import { Passenger } from '../../models/PassengerModel';
import showToast from '../../utils/toast';
import PassengersTable, { PassengersViewMode } from './PassengersTable';
import PassengerForm from '../../components/Passengers/PassengerForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination from '../../components/Base/Pagination';
import BulkPassengerModal from '../../components/Passengers/BulkPassengerModal';
import AuditInfoModal from '../../components/Base/AuditInfoModal';
import { isPlaceholderAddress } from '../../components/Passengers/bulkPassengerValidation';
import './styles/PassengersPage.css';

type SortOrder = 'asc' | 'desc';
type PassengerSortField = 'name' | 'first_lastname';

const PASSENGERS_VIEW_STORAGE_KEY = 'passengers_list_view_mode';

function readStoredViewMode(): PassengersViewMode {
  try {
    const stored = sessionStorage.getItem(PASSENGERS_VIEW_STORAGE_KEY);
    if (stored === 'table' || stored === 'cards') return stored;
  } catch {
    /* ignore */
  }
  return 'cards';
}

const PassengersPage: React.FC = () => {
  const svc = usePassengerService();
  const { list: getPassengers, store: createPassenger, update: updatePassenger, remove: removePassenger, create, edit, fetching } = svc;
  const [viewMode, setViewMode] = useState<PassengersViewMode>(readStoredViewMode);

  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [editing, setEditing] = useState<Passenger | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [passengerIdToDelete, setPassengerIdToDelete] = useState<number | null>(null);
  const [roleConflictInfo, setRoleConflictInfo] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [nameFilter, setNameFilter] = useState('');
  const nameFilterRef = useRef(nameFilter);
  nameFilterRef.current = nameFilter;
  const [firstLastNameFilter, setFirstLastNameFilter] = useState('');
  const firstLastNameFilterRef = useRef(firstLastNameFilter);
  firstLastNameFilterRef.current = firstLastNameFilter;
  const [secondLastNameFilter, setSecondLastNameFilter] = useState('');
  const secondLastNameFilterRef = useRef(secondLastNameFilter);
  secondLastNameFilterRef.current = secondLastNameFilter;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [companyFilter, setCompanyFilter] = useState('');
  const companyFilterRef = useRef(companyFilter);
  companyFilterRef.current = companyFilter;
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const sortOrderRef = useRef<SortOrder>('asc');
  const [sortField, setSortField] = useState<PassengerSortField>('name');
  const sortFieldRef = useRef<PassengerSortField>('name');
  const [sortAnimation, setSortAnimation] = useState({ key: 0, target: '' });

  /** 🔹 Cargar lista de pasajeros */
  const loadPassengers = useCallback((page: number = 1) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const name = nameFilterRef.current.trim();
    if (name) filters.name = name;
    const fn = firstLastNameFilterRef.current.trim();
    if (fn) filters.first_lastname = fn;
    const sn = secondLastNameFilterRef.current.trim();
    if (sn) filters.second_lastname = sn;
    if (companyFilterRef.current) filters.company_id = companyFilterRef.current;
    filters.sort_order = sortOrderRef.current;
    filters.sort_field = sortFieldRef.current;

    getPassengers(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.passengers) ? data.passengers : data.items ?? [];
          setPassengers(items);
          
          // Actualizar información de paginación
          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp: any) => {
          showToast(resp?.message ?? 'Error cargando pasajeros', 'error');
        },
      },
      ctrl.signal
    );
  }, [getPassengers]);

  const loadCompanyOptions = useCallback(() => {
    create({
      onSuccess: (resp: any) => {
        const list = resp?.data ?? [];
        const mapped = list.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        }));
        setCompanies(mapped);
      },
      onError: () => showToast('Error cargando empresas', 'error'),
    });
  }, [create]);

  useEffect(() => {
    loadPassengers();
    loadCompanyOptions();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadPassengers, loadCompanyOptions]);

  const handleNameChange = useCallback(
    (value: string) => {
      nameFilterRef.current = value;
      setNameFilter(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => loadPassengers(1), 400);
    },
    [loadPassengers]
  );

  const handleFirstLastNameChange = useCallback(
    (value: string) => {
      firstLastNameFilterRef.current = value;
      setFirstLastNameFilter(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => loadPassengers(1), 400);
    },
    [loadPassengers]
  );

  const handleSecondLastNameChange = useCallback(
    (value: string) => {
      secondLastNameFilterRef.current = value;
      setSecondLastNameFilter(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => loadPassengers(1), 400);
    },
    [loadPassengers]
  );

  const handleCompanyFilterChange = useCallback(
    (value: string) => {
      companyFilterRef.current = value;
      setCompanyFilter(value);
      loadPassengers(1);
    },
    [loadPassengers]
  );

  const handleSortToggle = useCallback((field: PassengerSortField) => {
    const nextOrder: SortOrder = sortFieldRef.current === field && sortOrderRef.current === 'asc' ? 'desc' : 'asc';
    sortFieldRef.current = field;
    sortOrderRef.current = nextOrder;
    setSortField(field);
    setSortOrder(nextOrder);
    setSortAnimation((current) => ({ key: current.key + 1, target: field }));
    loadPassengers(1);
  }, [loadPassengers]);

  const handleViewModeChange = useCallback((mode: PassengersViewMode) => {
    setViewMode(mode);
    try {
      sessionStorage.setItem(PASSENGERS_VIEW_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const clearFilters = useCallback(() => {
    nameFilterRef.current = '';
    firstLastNameFilterRef.current = '';
    secondLastNameFilterRef.current = '';
    companyFilterRef.current = '';
    setNameFilter('');
    setFirstLastNameFilter('');
    setSecondLastNameFilter('');
    setCompanyFilter('');
    loadPassengers(1);
  }, [loadPassengers]);

  const activeFilterLabel = [companyFilter, nameFilter, firstLastNameFilter, secondLastNameFilter]
    .map((v) => v.trim())
    .filter(Boolean)
    .join(' · ');

  /** 🔹 Crear pasajero */
  const openCreate = () => {
    svc.create({
      onSuccess: (resp: any) => {
        const list = resp?.data?? [];
        const mapped = list.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        }));
        setCompanies(mapped);

        setEditing({
          name: '',
          first_lastname: '',
          second_lastname: '',
          phone: '',
          email: '',
          company: '',
          addresses: [{ street: '' }],
          also_as_requester: false,
          also_as_authorizer: false,
        });

        setAuditModalOpen(false);
        setShowForm(true);
      },
      onError: () => showToast('Error cargando formulario', 'error'),
    });
  };

  /** 🔹 Editar pasajero */
  const openEdit = (p: Passenger) => {
    svc.edit(p.id!, {
      onSuccess: (resp: any) => {
        const data = resp.data ?? {};
        const passenger = data.passenger ?? {};
        const comps = data.companies ?? [];

        // Normaliza empresa para que el select la muestre correctamente
        const rawAddresses = (passenger.addresses ?? []).filter(
          (a: { street?: string }) => !isPlaceholderAddress(a.street ?? '')
        );
        const normalized = {
          ...passenger,
          company: passenger.company_id ?? passenger.company ?? '',
          addresses: rawAddresses.length > 0 ? rawAddresses : [{ street: '' }],
        };

        setEditing(normalized);
        setCompanies(comps.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        })));
        setAuditModalOpen(false);
        setShowForm(true);
      },
      onError: () => showToast('Error cargando datos del pasajero', 'error'),
    });
  };

  /** 🔹 Guardar */
  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setSavingLocked(true);
    clearErrors();
    try {
      const cleanedAddresses = (editing.addresses ?? []).filter(
        (a) => (a.street ?? '').trim() && !isPlaceholderAddress(a.street ?? '')
      );
      if (cleanedAddresses.length === 0) {
        showToast('Debe ingresar al menos una dirección real', 'error');
        return;
      }
      const payload = { ...editing, addresses: cleanedAddresses };

      if (editing.id) {
        const { also_as_requester: _r, also_as_authorizer: _a, ...updatePayload } = payload;
        const r = await updatePassenger(editing.id, updatePayload);
        return r;
      } else {
        const r = await createPassenger(payload);
        return r;
      }
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      // fallback
      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  /** Extrae mensaje para toast desde errors anidados (ej: errors.addresses.addresses) */
  const getErrorMessage = (r?: any): string => {
    if (r?.message && typeof r.message === 'string') return r.message;
    const errs = r?.errors ?? r?.data;
    if (errs && typeof errs === 'object') {
      const first = Object.values(errs)[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object') {
        const nested = Object.values(first)[0];
        if (typeof nested === 'string') return nested;
      }
    }
    return 'Error guardando';
  };

  const getRoleConflictMessage = (resp?: any): string | null => {
    const err = resp?.errors;
    if (!err || typeof err !== 'object') return null;
    const raw = err.role_conflict;
    if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'string') return raw[0];
    if (typeof raw === 'string') return raw;
    return null;
  };

  /** 🔹 Post-guardado */
  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      const d = resp?.data ?? {};
      const parts: string[] = [];
      if (d.requester_id != null) {
        parts.push('solicitante creado');
      }
      if (d.authorizer_id != null) {
        parts.push('autorizador creado');
      }
      const extra = parts.length ? ` (${parts.join(', ')})` : '';
      showToast((resp?.message ?? 'Guardado correctamente') + extra, 'success');
      if (d.id && editing?.id) {
        setEditing((prev) => (prev ? { ...prev, ...d } : prev));
      }
      setShowForm(false);
      setEditing(null);
      setAuditModalOpen(false);
      clearErrors();
      loadPassengers(pagination.page);
    } else {
      const roleMsg = getRoleConflictMessage(resp);
      if (roleMsg) {
        setRoleConflictInfo({ open: true, message: roleMsg });
        return;
      }
      setErrorsFromResponse(resp);
      showToast(getErrorMessage(resp), 'error');
    }
  };

  /** 🔹 Eliminar pasajero */
  const openDeleteConfirmation = (id: number) => {
    setPassengerIdToDelete(id);
    setIsAlertOpen(true);
  };


  const handleConfirmDelete = () => {
    if (passengerIdToDelete === null) return;

    removePassenger(passengerIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        loadPassengers(pagination.page);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setPassengerIdToDelete(null);
  };
  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setPassengerIdToDelete(null);
  };

  return (
    <div className="passengers-page">
      <header className="users-header">
        <h1>Pasajeros</h1>
        <div className="passengers-header-actions">
          <button type="button" className="btn-create" onClick={openCreate}>
            + Agregar 1 Pasajero
          </button>
          <button
            type="button"
            className="btn-create btn-create--outline"
            onClick={() => setBulkModalOpen(true)}
          >
            + Agregar multiples pasajeros
          </button>
        </div>
      </header>

      <section className="passengers-meta">
        <span>{fetching.list ? 'Cargando...' : `${passengers.length} pasajeros`}</span>
      </section>

      <PassengersTable
        data={passengers}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        sortOrder={sortOrder}
        sortField={sortField}
        sortAnimationKey={sortAnimation.key}
        sortAnimationTarget={sortAnimation.target}
        onSortToggle={handleSortToggle}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        totalCount={pagination.totalCount}
        searchTerm={activeFilterLabel}
        onClearSearch={clearFilters}
        toolbar={
          <div className="passengers-filters">
            <div className="passengers-filters__field passengers-filters__field--company">
              <label htmlFor="passengers-filter-company" className="passengers-filters__label">
                Empresa
              </label>
              <select
                id="passengers-filter-company"
                value={companyFilter}
                onChange={(e) => handleCompanyFilterChange(e.target.value)}
                className="passengers-filters__control passengers-filters__control--select"
              >
                <option value="">Todas las empresas</option>
                {companies.map((company) => (
                  <option key={company.value} value={company.value}>
                    {company.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="passengers-filters__field">
              <label htmlFor="passengers-filter-name" className="passengers-filters__label">
                Nombre
              </label>
              <input
                id="passengers-filter-name"
                type="text"
                value={nameFilter}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ej. Juan"
                autoComplete="off"
                className="passengers-filters__control"
              />
            </div>
            <div className="passengers-filters__field">
              <label htmlFor="passengers-filter-first-lastname" className="passengers-filters__label">
                Primer apellido
              </label>
              <input
                id="passengers-filter-first-lastname"
                type="text"
                value={firstLastNameFilter}
                onChange={(e) => handleFirstLastNameChange(e.target.value)}
                placeholder="Ej. Pérez"
                autoComplete="off"
                className="passengers-filters__control"
              />
            </div>
            <div className="passengers-filters__field">
              <label htmlFor="passengers-filter-second-lastname" className="passengers-filters__label">
                Segundo apellido
              </label>
              <input
                id="passengers-filter-second-lastname"
                type="text"
                value={secondLastNameFilter}
                onChange={(e) => handleSecondLastNameChange(e.target.value)}
                placeholder="Ej. González"
                autoComplete="off"
                className="passengers-filters__control"
              />
            </div>
          </div>
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                loadPassengers(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="passengers-modal">
          <div className="passengers-modal-inner">
            <div className="passengers-modal-title-row">
              <h3>{editing.id ? 'Editar pasajero' : 'Crear pasajero'}</h3>
              {editing.id ? (
                <button
                  type="button"
                  className="btn-audit-info"
                  onClick={() => setAuditModalOpen(true)}
                >
                  Ver historial
                </button>
              ) : null}
            </div>

            <PassengerForm
              value={editing}
              onChange={setEditing}
              error={undefined}
              companies={companies}
              fieldErrors={fieldErrors}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm
                callbackCancel={() => {
                  clearErrors();
                  setAuditModalOpen(false);
                  setShowForm(false);
                }}
                locked={savingLocked}
                confirmCancel={false}
              />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar pasajero?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />

      <AuditInfoModal
        isOpen={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
        title="Historial del pasajero"
        createdAt={editing?.created_at}
        updatedAt={editing?.updated_at}
        createdByName={editing?.created_by_name}
        updatedByName={editing?.updated_by_name}
      />

      <BulkPassengerModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        companies={companies}
        onSuccess={() => loadPassengers(1)}
      />

      {roleConflictInfo.open && (
        <div
          className="passengers-modal passengers-role-info-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="passenger-role-info-title"
          onClick={() => setRoleConflictInfo({ open: false, message: '' })}
        >
          <div className="passengers-modal-inner passengers-role-info-card" onClick={e => e.stopPropagation()}>
            <h3 id="passenger-role-info-title">Información</h3>
            <p className="passengers-role-info-text">{roleConflictInfo.message}</p>
            <div className="passengers-role-info-actions">
              <button
                type="button"
                className="btn-create"
                onClick={() => setRoleConflictInfo({ open: false, message: '' })}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengersPage;
